/**
 * TEMPORARY: realistic CSV content for the data export mock. Remove with
 * DataExportsMock.ts before merging to main.
 *
 * Builds one deterministic dataset per set of locations (clients → pets →
 * appointments → invoices, all cross-referenced) and slices it by the export
 * filters, so the row count shown in the list matches the downloaded file.
 * Columns follow the backend export serializers (API::Client::Json), with
 * readable headers and the location name the org export will add.
 */
import moment, { Moment } from 'moment-timezone';

export interface MockLocation {
    id: number;
    name: string;
}

interface MockFilters {
    marketplace_ids?: Array<number>;
    start_date?: string | null;
    end_date?: string | null;
}

type Row = Array<string | number>;

interface Record_ {
    locationId: number;
    createdAt: Moment;
    row: Row;
}

interface Dataset {
    customers: Array<Record_>;
    pets: Array<Record_>;
    appointments: Array<Record_>;
    invoices: Array<Record_>;
}

const FALLBACK_LOCATIONS: Array<MockLocation> = [
    { id: -1, name: 'Downtown' },
    { id: -2, name: 'Westside' },
    { id: -3, name: 'Lakeview' }
];

const HEADERS: Record<keyof Dataset, Array<string>> = {
    customers: [
        'Location',
        'Client ID',
        'First name',
        'Last name',
        'Email',
        'Phone',
        'Notes',
        'Email opt-out',
        'SMS opt-out',
        'Marketing opt-out',
        'Blocked',
        'Active',
        'Client since',
        'Created at'
    ],
    pets: [
        'Location',
        'Pet ID',
        'Name',
        'Owner ID',
        'Owner',
        'Type',
        'Breed',
        'Size',
        'Weight (lb)',
        'Gender',
        'Hair type',
        'Hair length',
        'Color',
        'Birthdate',
        'Allergies',
        'Grooming notes',
        'Deceased',
        'Active',
        'Created at'
    ],
    appointments: [
        'Location',
        'Appointment ID',
        'Booking ID',
        'Client ID',
        'Client',
        'Pet ID',
        'Pet',
        'Service',
        'Staff',
        'Starts at',
        'Ends at',
        'Status',
        'Notes',
        'Created at'
    ],
    invoices: [
        'Location',
        'Invoice ID',
        'Number',
        'Client ID',
        'Client',
        'Booking ID',
        'Issued at',
        'Subtotal',
        'Tip',
        'Total',
        'Void',
        'Created at'
    ]
};

const FIRST_NAMES = [
    'Emma',
    'Liam',
    'Olivia',
    'Noah',
    'Ava',
    'Ethan',
    'Sophia',
    'Mason',
    'Isabella',
    'Lucas',
    'Mia',
    'Logan',
    'Charlotte',
    'James',
    'Amelia',
    'Benjamin',
    'Harper',
    'Elijah',
    'Evelyn',
    'Daniel',
    'Abigail',
    'Henry',
    'Emily',
    'Jackson',
    'Madison',
    'Sebastian',
    'Camila',
    'Mateo',
    'Valentina',
    'Diego',
    'Sofia',
    'Gabriel'
];
const LAST_NAMES = [
    'Smith',
    'Johnson',
    'Williams',
    'Brown',
    'Jones',
    'Garcia',
    'Miller',
    'Davis',
    'Rodriguez',
    'Martinez',
    'Hernandez',
    'Lopez',
    'Gonzalez',
    'Wilson',
    'Anderson',
    'Thomas',
    'Taylor',
    'Moore',
    'Jackson',
    'Martin',
    'Lee',
    'Perez',
    'Thompson',
    'White',
    'Harris',
    'Sanchez',
    'Clark',
    'Ramirez',
    'Lewis',
    'Robinson'
];
const EMAIL_DOMAINS = ['gmail.com', 'yahoo.com', 'outlook.com', 'icloud.com', 'hotmail.com'];
const CLIENT_NOTES = [
    '',
    '',
    '',
    '',
    'Prefers text reminders',
    'Call before arrival',
    'VIP client',
    'Pays with card on file'
];

const CAT_BREEDS = ['Domestic Shorthair', 'Maine Coon', 'Ragdoll', 'Persian', 'Siamese', 'British Shorthair'];
const PET_NAMES = [
    'Bella',
    'Max',
    'Luna',
    'Charlie',
    'Lucy',
    'Cooper',
    'Daisy',
    'Milo',
    'Bailey',
    'Rocky',
    'Coco',
    'Teddy',
    'Rosie',
    'Bear',
    'Lola',
    'Duke',
    'Zoe',
    'Tucker',
    'Penny',
    'Oliver',
    'Nala',
    'Winston',
    'Ruby',
    'Finn',
    'Mochi',
    'Biscuit',
    'Pepper',
    'Oreo',
    'Hazel',
    'Leo'
];
// size, min weight, max weight (lb), breeds of that size
const DOG_SIZES: Array<[string, number, number, Array<string>]> = [
    [
        'Small',
        5,
        20,
        ['Shih Tzu', 'Yorkshire Terrier', 'Maltese', 'Pomeranian', 'Dachshund', 'Poodle (Toy)', 'Chihuahua']
    ],
    [
        'Medium',
        21,
        50,
        [
            'French Bulldog',
            'Cavalier King Charles Spaniel',
            'Cocker Spaniel',
            'Mini Goldendoodle',
            'Beagle',
            'Mixed Breed'
        ]
    ],
    [
        'Large',
        51,
        90,
        [
            'Golden Retriever',
            'Labrador Retriever',
            'German Shepherd',
            'Goldendoodle',
            'Australian Shepherd',
            'Siberian Husky'
        ]
    ],
    ['X-Large', 91, 140, ['Bernese Mountain Dog', 'Great Dane', 'Newfoundland', 'Bernedoodle']]
];
const HAIR_TYPES = ['Smooth', 'Wire', 'Curly', 'Double coat', 'Silky'];
const HAIR_LENGTHS = ['Short', 'Medium', 'Long'];
const COLORS = ['Black', 'White', 'Brown', 'Cream', 'Golden', 'Gray', 'Black & White', 'Brindle', 'Merle', 'Apricot'];
const ALLERGIES = ['', '', '', '', '', 'Chicken', 'Oatmeal shampoo', 'Fleas', 'Grain'];
const GROOMING_NOTES = [
    '',
    '',
    '',
    'Nervous with dryers, hand dry',
    'Teddy bear cut, 1/2" all over',
    'Do not shave',
    'Sensitive paws',
    'Puppy cut, keep ears long',
    'Muzzle for nails'
];

const SERVICES: Array<[string, number, number]> = [
    // name, duration in minutes, price
    ['Full Groom', 120, 85],
    ['Bath & Brush', 60, 55],
    ['Bath & Tidy', 75, 65],
    ['Nail Trim', 15, 18],
    ['De-shedding Treatment', 90, 75],
    ['Puppy Intro Groom', 60, 45],
    ['Daycare - Full Day', 540, 38],
    ['Daycare - Half Day', 300, 25],
    ['Boarding - Overnight', 1440, 65],
    ['Teeth Brushing', 15, 12]
];
const STAFF = [
    'Ashley Turner',
    'Brandon Scott',
    'Carla Méndez',
    'Derek Nguyen',
    'Erin Walsh',
    'Fernanda Ruiz',
    'Grace Kim',
    'Hannah Brooks',
    'Isaac Rivera',
    'Jasmine Patel'
];
const APPOINTMENT_NOTES = ['', '', '', '', '', 'Add bandana', 'Owner will pick up early', 'Check ears', 'First visit'];

// Small seeded PRNG (mulberry32) so the same locations always produce the same file.
const createRandom = (seed: number) => {
    let state = seed >>> 0;
    const next = () => {
        state = (state + 0x6d2b79f5) >>> 0;
        let t = state;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    const int = (min: number, max: number) => Math.floor(next() * (max - min + 1)) + min;
    // Small seeds give skewed first values; discard a few before using it.
    for (let i = 0; i < 20; i++) next();
    const pick = <T>(items: Array<T>): T => items[Math.floor(next() * items.length)];
    const chance = (probability: number) => next() < probability;
    return { next, int, pick, chance };
};

const DATETIME = 'YYYY-MM-DD HH:mm';
const DATE = 'YYYY-MM-DD';
const yesNo = (value: boolean) => (value ? 'Yes' : 'No');
const money = (value: number) => value.toFixed(2);

const buildDataset = (locations: Array<MockLocation>): Dataset => {
    const random = createRandom(locations.reduce((seed, location) => seed * 31 + Math.abs(location.id), 7));
    const now = moment();
    const dataset: Dataset = { customers: [], pets: [], appointments: [], invoices: [] };

    let customerId = 18240;
    let petId = 30410;
    let bookingId = 88120;
    let appointmentId = 120500;
    let invoiceId = 64300;
    let invoiceNumber = 10450;

    locations.forEach(location => {
        const clientsInLocation = random.int(45, 80);

        for (let c = 0; c < clientsInLocation; c++) {
            customerId += 1;
            const firstName = random.pick(FIRST_NAMES);
            const lastName = random.pick(LAST_NAMES);
            const clientName = `${firstName} ${lastName}`;
            const createdAt = now
                .clone()
                .subtract(random.int(1, 730), 'days')
                .hour(random.int(8, 18))
                .minute(random.int(0, 59));
            const blocked = random.chance(0.02);

            dataset.customers.push({
                locationId: location.id,
                createdAt,
                row: [
                    location.name,
                    customerId,
                    firstName,
                    lastName,
                    `${firstName}.${lastName}${random.int(1, 99)}@${random.pick(EMAIL_DOMAINS)}`.toLowerCase(),
                    `(${random.int(201, 989)}) ${random.int(200, 999)}-${random.int(1000, 9999)}`,
                    random.pick(CLIENT_NOTES),
                    yesNo(random.chance(0.08)),
                    yesNo(random.chance(0.06)),
                    yesNo(random.chance(0.12)),
                    yesNo(blocked),
                    yesNo(!blocked && !random.chance(0.05)),
                    createdAt.format(DATE),
                    createdAt.format(DATETIME)
                ]
            });

            const petsOfClient = random.chance(0.3) ? 2 : 1;
            const clientPets: Array<{ id: number; name: string }> = [];

            for (let p = 0; p < petsOfClient; p++) {
                petId += 1;
                const isCat = random.chance(0.15);
                const [size, minWeight, maxWeight, breeds] = isCat
                    ? (['Small', 7, 16, CAT_BREEDS] as [string, number, number, Array<string>])
                    : random.pick(DOG_SIZES);
                const name = random.pick(PET_NAMES);
                const petCreatedAt = createdAt.clone().add(random.int(0, 20), 'minutes');
                clientPets.push({ id: petId, name });

                dataset.pets.push({
                    locationId: location.id,
                    createdAt: petCreatedAt,
                    row: [
                        location.name,
                        petId,
                        name,
                        customerId,
                        clientName,
                        isCat ? 'Cat' : 'Dog',
                        random.pick(breeds),
                        size,
                        random.int(minWeight, maxWeight),
                        random.chance(0.5) ? 'Male' : 'Female',
                        random.pick(HAIR_TYPES),
                        random.pick(HAIR_LENGTHS),
                        random.pick(COLORS),
                        now.clone().subtract(random.int(120, 4800), 'days').format(DATE),
                        random.pick(ALLERGIES),
                        random.pick(GROOMING_NOTES),
                        yesNo(random.chance(0.01)),
                        'Yes',
                        petCreatedAt.format(DATETIME)
                    ]
                });
            }

            // Visits from client creation until now: regulars every 3-8 weeks, occasional clients less.
            const visitEveryDays = random.chance(0.6) ? random.int(21, 56) : random.int(60, 150);
            let visit = createdAt.clone().add(random.int(1, 14), 'days');

            while (visit.isBefore(now.clone().add(21, 'days'))) {
                const pet = random.pick(clientPets);
                const [service, duration, price] = random.pick(SERVICES);
                const startsAt = visit
                    .clone()
                    .hour(random.int(7, 16))
                    .minute(random.pick([0, 15, 30, 45]));
                const isFuture = startsAt.isAfter(now);
                const status = isFuture
                    ? 'Booked'
                    : random.chance(0.06)
                    ? 'Cancelled'
                    : random.chance(0.03)
                    ? 'No show'
                    : 'Completed';
                const bookedAt = startsAt.clone().subtract(random.int(1, 21), 'days');

                bookingId += 1;
                appointmentId += 1;

                dataset.appointments.push({
                    locationId: location.id,
                    createdAt: bookedAt,
                    row: [
                        location.name,
                        appointmentId,
                        bookingId,
                        customerId,
                        clientName,
                        pet.id,
                        pet.name,
                        service,
                        random.pick(STAFF),
                        startsAt.format(DATETIME),
                        startsAt.clone().add(duration, 'minutes').format(DATETIME),
                        status,
                        random.pick(APPOINTMENT_NOTES),
                        bookedAt.format(DATETIME)
                    ]
                });

                if (status === 'Completed') {
                    invoiceId += 1;
                    invoiceNumber += 1;
                    const issuedAt = startsAt.clone().add(duration, 'minutes');
                    const subtotal = price + (random.chance(0.25) ? random.pick([10, 12, 15, 18]) : 0);
                    const tip = random.chance(0.7) ? Math.round(subtotal * random.pick([0.15, 0.18, 0.2, 0.25])) : 0;
                    const voided = random.chance(0.01);

                    dataset.invoices.push({
                        locationId: location.id,
                        createdAt: issuedAt,
                        row: [
                            location.name,
                            invoiceId,
                            `INV-${invoiceNumber}`,
                            customerId,
                            clientName,
                            bookingId,
                            issuedAt.format(DATETIME),
                            money(subtotal),
                            money(tip),
                            money(subtotal + tip),
                            yesNo(voided),
                            issuedAt.format(DATETIME)
                        ]
                    });
                }

                visit = visit.clone().add(visitEveryDays + random.int(-5, 5), 'days');
            }
        }
    });

    return dataset;
};

let cached: { key: string; dataset: Dataset } | null = null;

const datasetFor = (locations: Array<MockLocation>): { dataset: Dataset; locations: Array<MockLocation> } => {
    const effective = locations.length > 0 ? locations : FALLBACK_LOCATIONS;
    const key = effective.map(location => location.id).join(',');

    if (cached?.key !== key) {
        cached = { key, dataset: buildDataset(effective) };
    }

    return { dataset: cached.dataset, locations: effective };
};

const escapeCsv = (value: string | number): string => {
    const text = `${value}`;
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const selectRecords = (resource: string, filters: MockFilters | null, locations: Array<MockLocation>) => {
    const { dataset, locations: effective } = datasetFor(locations);
    const records = dataset[resource as keyof Dataset] ?? [];

    const requested = filters?.marketplace_ids ?? [];
    // Ids the dataset doesn't know (locations not loaded yet) fall back to every location.
    const locationIds = requested.filter(id => effective.some(location => location.id === id));
    const from = filters?.start_date ? moment(filters.start_date, DATE).startOf('day') : null;
    const to = filters?.end_date ? moment(filters.end_date, DATE).endOf('day') : null;

    return records.filter(
        record =>
            (locationIds.length === 0 || locationIds.includes(record.locationId)) &&
            (!from || !record.createdAt.isBefore(from)) &&
            (!to || !record.createdAt.isAfter(to))
    );
};

export const countMockRows = (resource: string, filters: MockFilters | null, locations: Array<MockLocation>): number =>
    selectRecords(resource, filters, locations).length;

/** Blob URL of the CSV, with a BOM so Excel opens accents correctly. */
export const buildMockCsvUrl = (
    resource: string,
    filters: MockFilters | null,
    locations: Array<MockLocation>
): string => {
    const header = HEADERS[resource as keyof Dataset] ?? ['Location'];
    const lines = [header, ...selectRecords(resource, filters, locations).map(record => record.row)].map(row =>
        row.map(escapeCsv).join(',')
    );

    return URL.createObjectURL(new Blob(['﻿', lines.join('\n')], { type: 'text/csv;charset=utf-8' }));
};
